import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getCurrentUser, saveCurrentUser } from "@/lib/utils";

export interface LanguageOption {
  id?: number | string;
  languageCode: string;
  languageName: string;
  translations?: Record<string, string>;
}

interface LanguageContextValue {
  languages: LanguageOption[];
  selectedLanguage: LanguageOption | null;
  selectedLanguageCode: string;
  changeLanguage: (languageCode: string) => void;
  isLoading: boolean;
  error: string | null;
  t: (keys: string | string[], fallback?: string) => string;
}

const SELECTED_LANGUAGE_STORAGE_KEY = "selected_language_code";

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

const DEFAULT_LANGUAGE: LanguageOption = {
  languageCode: "en",
  languageName: "English",
  translations: {},
};

const getStoredLanguage = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(SELECTED_LANGUAGE_STORAGE_KEY) || "";
};

const persistSelectedLanguage = (languageCode: string) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(SELECTED_LANGUAGE_STORAGE_KEY, languageCode);
  }

  const currentUser = getCurrentUser() || {};

  saveCurrentUser({
    language: languageCode,
    userSettings: {
      ...(currentUser.userSettings || {}),
      language: languageCode,
    },
  });
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    () =>
      getStoredLanguage() ||
      getCurrentUser()?.userSettings?.language ||
      getCurrentUser()?.language ||
      DEFAULT_LANGUAGE.languageCode,
  );

  const selectedLanguage = useMemo(
    () => ({
      ...DEFAULT_LANGUAGE,
      languageCode: selectedLanguageCode || DEFAULT_LANGUAGE.languageCode,
    }),
    [selectedLanguageCode],
  );

  const changeLanguage = useCallback((languageCode: string) => {
    const nextLanguageCode = languageCode.trim() || DEFAULT_LANGUAGE.languageCode;
    setSelectedLanguageCode(nextLanguageCode);
    persistSelectedLanguage(nextLanguageCode);
  }, []);

  const t = useCallback(
    (keys: string | string[], fallback?: string) => {
      const keyList = Array.isArray(keys) ? keys : [keys];
      return fallback || keyList[0] || "";
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      languages: [selectedLanguage],
      selectedLanguage,
      selectedLanguageCode,
      changeLanguage,
      isLoading: false,
      error: null,
      t,
    }),
    [changeLanguage, selectedLanguage, selectedLanguageCode, t],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
